import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAtomValue } from 'jotai';
import { CustomTravelEstimateType } from '@pkgtour-workspace/common/dist/constants/estimate';
import { CustomTravelEstimatePopupStep } from '@/components/CustomTravel/EstimatePopup';
import { customTravelFormStateAtom, CustomTravelFormState } from '@/atoms/customTravelFormState';
import useUser from '@/hooks/useUser';
import { redirectToLogin } from '@/utils/redirect';

interface validateCustomTravelUrlHocOptions {}

const CUSTOM_TRAVEL_FORM_BASE_PATH = '/custom-travel/[agtName]/[productId]';

enum CustomTravelPath {
  INDEX = CUSTOM_TRAVEL_FORM_BASE_PATH,
  ESTIMATE = `${CUSTOM_TRAVEL_FORM_BASE_PATH}/estimate`,
  CONSULTING = `${CUSTOM_TRAVEL_FORM_BASE_PATH}/consulting`,
}

function getSelfConditionsByPathAndStep(customTravelFormState: CustomTravelFormState) {
  const notAvailableStepConditions = [true];
  const conditions = [
    !customTravelFormState.requirements,
    !customTravelFormState.estimateType,
    !customTravelFormState.groupType,
    !customTravelFormState.destinations,
    !customTravelFormState.tourSchedule,
    !customTravelFormState.personalCount,
    !customTravelFormState.specialNote,
    !customTravelFormState.budget,
  ];

  const conditionsByPathAndStep = {
    [CustomTravelPath.INDEX]: {
      [CustomTravelEstimatePopupStep.SelectCustomType]: conditions.slice(0, 1),
      [CustomTravelEstimatePopupStep.SelectGroupType]: conditions.slice(0, 2),
      [CustomTravelEstimatePopupStep.SelectLocation]: conditions.slice(0, 3),
      [CustomTravelEstimatePopupStep.SelectDuration]: conditions.slice(0, 4),
      [CustomTravelEstimatePopupStep.SelectPassenger]: conditions.slice(0, 5),
      [CustomTravelEstimatePopupStep.SelectBudget]: conditions.slice(0, 7),
    },
    [CustomTravelPath.ESTIMATE]: conditions.slice(0, 8),
    [CustomTravelPath.CONSULTING]: {
      [CustomTravelEstimateType.ADVISOR]: notAvailableStepConditions,
      [CustomTravelEstimateType.SELF]: conditions.slice(0, 8),
    },
  };

  return conditionsByPathAndStep;
}

function getAdvisorConditionsByPathAndStep(customTravelFormState: CustomTravelFormState) {
  const notAvailableStepConditions = [true];
  const conditions = [
    !customTravelFormState.requirements,
    !customTravelFormState.estimateType,
    !customTravelFormState.groupType,
    !customTravelFormState.tourSchedule,
    !customTravelFormState.personalCount,
    !customTravelFormState.specialNote,
  ];

  const conditionsByPathAndStep = {
    [CustomTravelPath.INDEX]: {
      [CustomTravelEstimatePopupStep.SelectCustomType]: conditions.slice(0, 1),
      [CustomTravelEstimatePopupStep.SelectGroupType]: conditions.slice(0, 2),
      [CustomTravelEstimatePopupStep.SelectLocation]: notAvailableStepConditions,
      [CustomTravelEstimatePopupStep.SelectDuration]: conditions.slice(0, 3),
      [CustomTravelEstimatePopupStep.SelectPassenger]: conditions.slice(0, 4),
      [CustomTravelEstimatePopupStep.SelectBudget]: notAvailableStepConditions,
    },
    [CustomTravelPath.ESTIMATE]: notAvailableStepConditions,
    [CustomTravelPath.CONSULTING]: {
      [CustomTravelEstimateType.ADVISOR]: conditions.slice(0, 6),
      [CustomTravelEstimateType.SELF]: notAvailableStepConditions,
    },
  };

  return conditionsByPathAndStep;
}

export const validateCustomTravelUrlHoc =
  <Props extends object>(WrappedComponent: React.ComponentType<Props>, options?: validateCustomTravelUrlHocOptions) =>
  (props: Props) => {
    const router = useRouter();
    const customTravelFormState = useAtomValue(customTravelFormStateAtom);
    const { me } = useUser();

    useEffect(() => {
      if (!me) {
        return;
      }
      if (!me?.isLogin) {
        alert('로그인 후 이용해주시기 바랍니다.');
        redirectToLogin();
        return;
      }
      if (!me?.canUseCustomTravel) {
        router.replace('/custom-travel/error?ageRestriction=true');
      }
    }, [me, router]);

    useEffect(() => {
      const { agtName, productId, step, type } = router.query;
      const currentStep = step as CustomTravelEstimatePopupStep | undefined;
      const currentType = (type || customTravelFormState.estimateType) as CustomTravelEstimateType | undefined;
      const conditionsByPathAndStep =
        currentType === CustomTravelEstimateType.ADVISOR
          ? getAdvisorConditionsByPathAndStep(customTravelFormState)
          : getSelfConditionsByPathAndStep(customTravelFormState);

      const replacePath = `/custom-travel/${agtName}/${productId}`;

      if (
        router.pathname === CustomTravelPath.INDEX &&
        currentStep &&
        conditionsByPathAndStep[router.pathname][currentStep]?.some((v) => v)
      ) {
        router.replace(replacePath);
      }
      if (router.pathname === CustomTravelPath.ESTIMATE && conditionsByPathAndStep[router.pathname].some((v) => v)) {
        router.replace(replacePath);
      }
      if (
        router.pathname === CustomTravelPath.CONSULTING &&
        (!currentType || (currentType && conditionsByPathAndStep[router.pathname][currentType].some((v) => v)))
      ) {
        router.replace(replacePath);
      }
    }, [router, customTravelFormState]);

    return <WrappedComponent {...props} />;
  };
